import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  LockOutlined as LockOutlinedIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  Payments as PaymentsIcon,
  ReceiptLong as ReceiptLongIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { useVersionContable } from './versionContable';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import RegistrarCuentasXPagar from './RegistrarCuentasXPagar';
import VistaFacturaCuentasXPagar from './VistaFacturaCuentasXPagar';

const PROGRAMA = 'mantenimientocuentasxpagar';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const tramoLabel = (t) => {
  if (t === '90+') return 'Más de 90 días';
  if (!t) return '—';
  return `${t} días`;
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const primerDiaAno = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const btnPrimarySx = {
  height: 46,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
};

const btnOutlinedSx = {
  height: 46,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const MantenimientoCuentasXPagar = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA);
  // La versión fiscal es solo de consulta: ni registrar ni anular.
  const { soloConsulta } = useVersionContable();
  const puedeModificar =
    !soloConsulta && puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeRegistrar =
    puedeModificar || (!soloConsulta && puedeEditarProgramaContabilidad(roleDetalles, 'registrarcuentasxpagar'));
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fechaCorteAntiguedad, setFechaCorteAntiguedad] = useState(null);

  const [fechaDesde, setFechaDesde] = useState(primerDiaAno);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);
  const [tramoAntiguedad, setTramoAntiguedad] = useState('');
  const [tipoPago, setTipoPago] = useState('');
  const [dialogoNueva, setDialogoNueva] = useState(false);
  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [filaVista, setFilaVista] = useState(null);

  const cargarSuplidores = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudieron cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      setSuplidores([]);
      showError('Error de conexión al cargar suplidores');
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const buscar = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        fecha_desde: (fechaDesde || '').trim(),
        fecha_hasta: (fechaHasta || '').trim(),
        tramo_antiguedad: (tramoAntiguedad || '').trim(),
        tipodepago: (tipoPago || '').trim(),
      };
      if (suplidorSel?.id) body.suplidor_id = String(suplidorSel.id);
      const response = await fetch(`${servidor}/contabilidadbackend_listarmantenimientocuentasxpagar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'Error al cargar facturas');
        setFilas([]);
        setFechaCorteAntiguedad(null);
        return;
      }
      setFilas(data.facturas || []);
      setFechaCorteAntiguedad(data.fecha_corte_antiguedad || null);
    } catch (error) {
      setFilas([]);
      setFechaCorteAntiguedad(null);
      showError('Error de conexión al cargar facturas: ' + error.message);
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    puedeOperarDb,
    servidor,
    selectedCompany,
    usuarioLog,
    claveLog,
    fechaDesde,
    fechaHasta,
    tramoAntiguedad,
    tipoPago,
    suplidorSel,
    showError,
  ]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarSuplidores();
    }
  }, [puedeLeer, puedeOperarDb, cargarSuplidores]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      buscar();
    }
  }, [puedeLeer, puedeOperarDb]);

  const abrirNueva = () => {
    if (!puedeRegistrar) {
      showError('No tiene permiso para registrar cuentas por pagar');
      return;
    }
    setDialogoNueva(true);
  };

  const onGuardadoCxp = () => {
    setDialogoNueva(false);
    buscar();
  };

  const verFactura = (row) => {
    setFilaVista(row);
    setVistaFacturaAbierta(true);
  };

  const cerrarVistaFactura = () => {
    setVistaFacturaAbierta(false);
    setFilaVista(null);
  };

  const eliminarFactura = async (row) => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    if (!row.puede_eliminar) {
      const contado = (row.tipodefactura || '').toLowerCase() === 'contado';
      showError(
        contado
          ? 'No se puede anular: en factura a contado no debe haber pagos PAGO CXP adicionales sobre el mismo Nº (revise pagossup / aplicación de abonos).'
          : 'No se puede anular: la factura tiene pagos registrados (monto vplicado distinto de cero o abonos en pagossup).'
      );
      return;
    }
    const ok = window.confirm(
      `¿Anular el documento ${row.nfactura} del suplidor ${row.suplidor}? ` +
        'Los importes y la data fiscal quedarán en cero; el registro se conserva en factcxp. Avances y N/C vuelven a pendientes.'
    );
    if (!ok) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_eliminarmantenimientocuentasxpagar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          suplidor_id: String(row.suplidor),
          nfactura: String(row.nfactura || '').trim(),
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo anular');
        return;
      }
      showSuccess(data.mensaje || 'Documento anulado');
      await buscar();
    } catch {
      showError('Error de conexión al anular');
    }
  };

  const nombreEmpresa =
    selectedCompany?.name || selectedCompany?.empresa || nombreNegocio || 'Empresa';

  const subtituloPrint = useMemo(() => {
    const sup = suplidorSel ? opcionLabel(suplidorSel) : 'Todos los suplidores';
    const fd = fechaDesde ? `Desde ${formatearFecha(fechaDesde)}` : '';
    const fh = fechaHasta ? `Hasta ${formatearFecha(fechaHasta)}` : '';
    const tr = tramoAntiguedad ? `Tramo: ${tramoLabel(tramoAntiguedad)}` : 'Tramo: todos';
    const tp = tipoPago ? `Tipo: ${tipoPago}` : 'Tipo: todos';
    const corte =
      fechaCorteAntiguedad != null ? ` · Antigüedad al ${formatearFecha(fechaCorteAntiguedad)}` : '';
    return `${sup} · ${fd} · ${fh} · ${tr} · ${tp}${corte} · ${filas.length} registro(s)`;
  }, [suplidorSel, fechaDesde, fechaHasta, tramoAntiguedad, tipoPago, fechaCorteAntiguedad, filas.length]);

  const columnasPrint = [
    { id: 'suplidor', label: 'Suplidor', align: 'center' },
    { id: 'nombre_suplidor', label: 'Nombre' },
    { id: 'nfactura', label: 'Factura' },
    { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
    { id: 'tipodefactura', label: 'Tipo', align: 'center' },
    { id: 'tramo_antiguedad', label: 'Tramo', align: 'center', render: (v) => tramoLabel(v) },
    { id: 'dias_antiguedad', label: 'Días', align: 'center' },
    { id: 'valor', label: 'Valor', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'vplicado', label: 'Pagado', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'v_avance_distribucion', label: 'Avance (dist.)', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'v_notas_credito_suplidor', label: 'N/C suplidor', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'saldo_pendiente', label: 'Saldo', align: 'right', render: (v) => `$${fmtMoney(v)}` },
  ];

  const totalSaldoReporte = useMemo(
    () => filas.reduce((s, r) => s + (parseFloat(r.saldo_pendiente) || 0), 0),
    [filas]
  );

  const totalPagadoReporte = useMemo(
    () => filas.reduce((s, r) => s + (parseFloat(r.vplicado) || 0), 0),
    [filas]
  );

  const totalAvanceReporte = useMemo(
    () => filas.reduce((s, r) => s + (parseFloat(r.v_avance_distribucion) || 0), 0),
    [filas]
  );

  const totalNotasCreditoReporte = useMemo(
    () =>
      filas.reduce((s, r) => {
        const ncp = parseFloat(r.ncpagada);
        if (!Number.isNaN(ncp) && ncp > 0.005) return s + ncp;
        return s + (parseFloat(r.vnc_distribucion) || 0) + (parseFloat(r.v_notas_credito_suplidor) || 0);
      }, 0),
    [filas]
  );

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const cellEllipsisSx = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Mantenimiento de Cuentas x Pagar"
          subtitle={nombreNegocio ? `${nombreNegocio} · Cuentas por pagar` : 'Cuentas por pagar'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «mantenimientocuentasxpagar» en roles para acceder al mantenimiento.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Mantenimiento de Cuentas x Pagar"
          subtitle={nombreNegocio ? `${nombreNegocio} · Cuentas por pagar` : 'Cuentas por pagar'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Alert severity="info">Seleccione una empresa con base de datos asignada.</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Mantenimiento de Cuentas x Pagar"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Documento CUENTAS X PAGAR (factcxp)${
                fechaCorteAntiguedad != null
                  ? ` · Antigüedad al ${formatearFecha(fechaCorteAntiguedad)}`
                  : ''
              }`
            : `Documento CUENTAS X PAGAR (factcxp)${
                fechaCorteAntiguedad != null
                  ? ` · Antigüedad al ${formatearFecha(fechaCorteAntiguedad)}`
                  : ''
              }`
        }
        cta={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirNueva}
            disabled={!puedeRegistrar || !usuarioLog}
            sx={{ ...btnPrimarySx, height: 42 }}
          >
            Agregar
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <PrintListado
                title={nombreEmpresa}
                reportTitle="Listado — mantenimiento cuentas por pagar"
                subtitle={subtituloPrint}
                columns={columnasPrint}
                data={filas}
                emptyMessage="No hay datos para imprimir"
                footer={`Total pagado: $${fmtMoney(totalPagadoReporte)} · Total avance (dist.): $${fmtMoney(totalAvanceReporte)} · Total notas de crédito: $${fmtMoney(totalNotasCreditoReporte)} · Total saldo: $${fmtMoney(totalSaldoReporte)}`}
                printButtonLabel="Imprimir listado"
                printButtonProps={{
                  disabled: filas.length === 0,
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  cargarSuplidores();
                  buscar();
                }}
                disabled={cargando}
                sx={btnOutlinedSx}
              >
                Actualizar
              </Button>
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha desde"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha hasta"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="tramo-label" shrink>
                    Antigüedad
                  </InputLabel>
                  <Select
                    labelId="tramo-label"
                    label="Antigüedad"
                    value={tramoAntiguedad}
                    onChange={(e) => setTramoAntiguedad(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="0-30">0 a 30 días</MenuItem>
                    <MenuItem value="31-60">31 a 60 días</MenuItem>
                    <MenuItem value="61-90">61 a 90 días</MenuItem>
                    <MenuItem value="90+">Más de 90 días</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="tipo-pago-label" shrink>
                    Tipo factura
                  </InputLabel>
                  <Select
                    labelId="tipo-pago-label"
                    label="Tipo factura"
                    value={tipoPago}
                    onChange={(e) => setTipoPago(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Credito">Crédito</MenuItem>
                    <MenuItem value="Contado">Contado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={suplidores}
                  loading={cargandoSupl}
                  getOptionLabel={(o) => opcionLabel(o)}
                  value={suplidorSel}
                  onChange={(_, v) => setSuplidorSel(v)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor (opcional)"
                      placeholder="Todos"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => buscar()}
                  disabled={cargando}
                  sx={btnPrimarySx}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
          }}
        >
          <strong>Crédito (CUENTAS X PAGAR):</strong> se puede anular si no hay pagos.
        </Alert>

        {filas.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total pagado"
                value={`$${fmtMoney(totalPagadoReporte)}`}
                icon={PaymentsIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total avance (dist.)"
                value={`$${fmtMoney(totalAvanceReporte)}`}
                icon={ReceiptLongIcon}
                accent={FP.color.warning}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total N/C"
                value={`$${fmtMoney(totalNotasCreditoReporte)}`}
                icon={AccountBalanceIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total saldo"
                value={`$${fmtMoney(totalSaldoReporte)}`}
                icon={AttachMoneyIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de cuentas por pagar
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1320, sm: 1540 } }}>
                  <colgroup>
                    {['15%', '9%', '8%', '7%', '8%', '5%', '8%', '8%', '8%', '8%', '8%', '8%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Suplidor
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Factura
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fecha
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Tipo
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Tramo
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Días
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Valor
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Pagado
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Avance (dist.)
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          N/C suplidor
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Saldo
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print">
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {filas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem', py: 2 }}>
                            No hay registros con los filtros indicados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filas.map((row, idx) => {
                        const suplidorTxt = row.nombre_suplidor || row.suplidor || '—';
                        const facturaTxt = row.nfactura || '—';
                        const tipoTxt = row.tipodefactura || '—';
                        const tramoTxt = tramoLabel(row.tramo_antiguedad);
                        return (
                          <TableRow key={`${row.suplidor}-${row.nfactura}-${row.fecha}-${idx}`} hover>
                            <TableCell sx={cellEllipsisSx} title={suplidorTxt}>
                              {suplidorTxt}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 700, color: FP.color.textPrimary, ...cellEllipsisSx }}
                              title={facturaTxt}
                            >
                              {facturaTxt}
                            </TableCell>
                            <TableCell align="center">{formatearFecha(row.fecha)}</TableCell>
                            <TableCell sx={cellEllipsisSx} title={tipoTxt}>
                              {tipoTxt}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={tramoTxt}>
                              {tramoTxt}
                            </TableCell>
                            <TableCell align="center">{row.dias_antiguedad}</TableCell>
                            <TableCell align="right">
                              ${fmtMoney(row.valor + row.v_avance_distribucion + row.v_notas_credito_suplidor)}
                            </TableCell>
                            <TableCell align="right">${fmtMoney(row.vplicado)}</TableCell>
                            <TableCell align="right">${fmtMoney(row.v_avance_distribucion)}</TableCell>
                            <TableCell align="right">${fmtMoney(row.v_notas_credito_suplidor)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                              ${fmtMoney(row.saldo_pendiente)}
                            </TableCell>
                            <TableCell align="center" className="no-print">
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <Tooltip title="Ver detalle de la factura">
                                  <IconButton
                                    size="small"
                                    onClick={() => verFactura(row)}
                                    aria-label="Ver detalle de la factura"
                                    sx={{
                                      color: FP.color.navy,
                                      '&:hover': { bgcolor: FP.color.surfaceAlt },
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    row.puede_eliminar
                                      ? 'Anular documento (importes a cero)'
                                      : 'No se puede anular este documento'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!puedeModificar || !row.puede_eliminar}
                                      onClick={() => eliminarFactura(row)}
                                      aria-label="Anular documento (importes a cero)"
                                      sx={{
                                        color: FP.color.danger,
                                        '&:hover': { bgcolor: FP.color.dangerSoft },
                                        '&.Mui-disabled': { color: FP.color.textMuted },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <RegistrarCuentasXPagar
        open={dialogoNueva}
        onClose={() => setDialogoNueva(false)}
        onGuardado={onGuardadoCxp}
      />
      <VistaFacturaCuentasXPagar
        open={vistaFacturaAbierta}
        onClose={cerrarVistaFactura}
        suplidorId={filaVista?.suplidor || ''}
        nfactura={filaVista?.nfactura || ''}
        factura={filaVista}
      />
    </Box>
  );
};

export default MantenimientoCuentasXPagar;
